import { Team } from '@/common/types/databaseTypes';
import { useWorkspaceStore } from '@/features/workspace/hooks';
import { useTeamStore } from '@/stores/teamStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { teamService } from './teamService';

// 단일 팀 조회 훅
export function useTeam(teamId: string, options?: { enabled?: boolean }) {
    return useQuery<Team, Error>({
        queryKey: ['team', teamId],
        queryFn: () => teamService.getTeamById(teamId),
        enabled: !!teamId && (options?.enabled ?? true), // teamId가 있을 때만 쿼리 실행
        retry: false, // 에러 발생 시 재시도하지 않음
    });
}

// 내 팀 조회 훅
export function useMyTeams(enabled?: boolean) {
    return useQuery<Team[], Error>({
        queryKey: ['myTeams'],
        queryFn: () => teamService.getMyTeams(),
        enabled: enabled ?? true,
    });
}

// 워크스페이스의 내 팀 목록 조회 훅
export function useMyTeamsByWorkspaceId(workspaceId: string) {
    return useQuery<Team[], Error>({
        queryKey: ['myTeams', workspaceId],
        queryFn: () => teamService.getMyTeamsByWorkspaceId(workspaceId),
        enabled: !!workspaceId, // 워크스페이스 ID가 있을 때만 쿼리 실행
    });
}

// 팀 생성 뮤테이션 훅
export function useCreateTeam() {
    const queryClient = useQueryClient();

    return useMutation<
        Team,
        Error,
        {
            name: string;
            description?: string;
            profileImageUrl?: string;
            workspaceId: string;
        }
    >({
        mutationFn: ({ name, description, profileImageUrl, workspaceId }) =>
            teamService.createTeam(
                name,
                workspaceId,
                description,
                profileImageUrl
            ),
        onSuccess: async newTeam => {
            // 팀 목록과 내 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['myTeams'] });
            // 워크스페이스의 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['workspace', newTeam.workspace_id],
            });
        },
    });
}

// 팀 업데이트 뮤테이션 훅
export function useUpdateTeam() {
    const queryClient = useQueryClient();

    const setCurrentTeam = useTeamStore.getState().setCurrentTeam;
    const updateTeamInList = useTeamStore.getState().updateTeamInList;

    return useMutation<
        Team,
        Error,
        {
            teamId: string;
            name?: string;
            description?: string;
            profileImageUrl?: string;
        }
    >({
        mutationFn: ({ teamId, name, description, profileImageUrl }) =>
            teamService.updateTeam(teamId, name, description, profileImageUrl),
        onSuccess: async updatedTeam => {
            //전체 팀 목록에서 수정
            updateTeamInList(updatedTeam);
            // 선택 팀에서 수정
            await setCurrentTeam(updatedTeam);

            // 개별 팀, 팀 목록, 내 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['myTeams'] });
            await queryClient.invalidateQueries({
                queryKey: ['team', updatedTeam.id],
            });
        },
    });
}

// 팀 제목 수정 뮤테이션 훅
export function useUpdateTeamName() {
    const queryClient = useQueryClient();

    const setCurrentTeam = useTeamStore.getState().setCurrentTeam;
    const updateTeamInList = useTeamStore.getState().updateTeamInList;

    return useMutation<Team, Error, { teamId: string; name: string }>({
        mutationFn: ({ teamId, name }) => teamService.updateTeam(teamId, name),
        onSuccess: async updatedTeam => {
            //전체 팀 목록에서 수정
            updateTeamInList(updatedTeam);
            // 선택 팀에서 수정
            await setCurrentTeam(updatedTeam);

            // 개별 팀, 팀 목록, 내 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['myTeams'] });
            await queryClient.invalidateQueries({
                queryKey: ['team', updatedTeam.id],
            });
        },
    });
}

// 팀 삭제 뮤테이션 훅
export function useDeleteTeam() {
    const queryClient = useQueryClient();
    const setCurrentTeam = useTeamStore.getState().setCurrentTeam;
    const { currentWorkspace } = useWorkspaceStore();
    const navigate = useNavigate();

    return useMutation<string, Error, string>({
        mutationFn: teamId => teamService.deleteTeam(teamId),
        onSuccess: async teamId => {
            //전체 팀 목록에서 삭제
            await setCurrentTeam(null);

            void navigate(-1);

            // 팀 목록과 내 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['myTeams'] });
            // 워크스페이스의 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['workspace', currentWorkspace?.id],
            });
        },
    });
}

// 팀 프로필 업데이트 뮤테이션 훅
export function useUpdateTeamProfile() {
    const queryClient = useQueryClient();

    return useMutation<
        Team,
        Error,
        {
            id: string;
            name: string;
            description?: string;
            profileImageUrl?: string;
        }
    >({
        mutationFn: ({ id, name, description, profileImageUrl }) =>
            teamService.updateProfile(id, name, description, profileImageUrl),
        onSuccess: async updatedTeam => {
            // 개별 팀, 팀 목록, 내 팀 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['myTeams'] });
            await queryClient.invalidateQueries({
                queryKey: ['team', updatedTeam.id],
            });
        },
    });
}
